// ===================================================================
//  Diagram component-type to color mapping
//  Palette synced with the bot team's checker tool.
//
//  Since 2026-08-21 the bot MAY serve a per-hotspot `color` from its own
//  canonical component-type palette (API_CONTRACTS.md "Diagram Overlay
//  API"). A well-formed served color WINS over the map below; the map
//  stays the fallback for every type the bot does not consider canonical
//  (there the field is OMITTED, never defaulted) — so nothing the map
//  already knows regresses. Spec:
//  IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/DIAG_OVERLAY_PARTA_SERVED_COLOR_SPEC.md
// ===================================================================

// Border colors by component type
const DIAGRAM_TYPE_COLORS: Record<string, string> = {
  relay: '#FF8C00',
  contactor: '#FF8C00',
  module: '#E040FB',
  terminal_block: '#00BCD4',
  cable: '#BB66FF',
  switch: '#4CAF50',
  sensor: '#4CAF50',
  circuit_breaker: '#009688',
  fuse: '#FFC107',
  motor: '#8BC34A',
  transformer: '#EC407A',
  plc_module: '#E040FB',
  valve: '#4CAF50',
};
const DEFAULT_COLOR = '#9E9E9E';

/** `#rrggbb` — the contract says lowercase, we tolerate any case. */
const SERVED_COLOR_RE = /^#[0-9a-f]{6}$/i;

/**
 * OVERLAY-BOX-STYLE-1: the hotspot's served color when well-formed, else
 * undefined. NO local fallback on purpose — the unselected box needs to know
 * "served or not" (no served color = dashed grey box, never the map).
 */
export const getServedDiagramColor = (served?: string): string | undefined =>
  served && SERVED_COLOR_RE.test(served) ? served : undefined;

/**
 * Return the display color for a diagram hotspot.
 *
 * Resolution order: a valid server-served color wins, else the local
 * type -> color map, else the default grey. `served` is OPTIONAL and must
 * never be assumed present — absent, empty or malformed values fall back
 * to the map exactly as before this parameter existed.
 */
export const getDiagramColor = (type?: string, served?: string): string =>
  getServedDiagramColor(served) ||
  (type && DIAGRAM_TYPE_COLORS[type]) ||
  DEFAULT_COLOR;

/** Semi-transparent background (12% opacity of the winning color). */
export const getDiagramBgColor = (type?: string, served?: string): string => {
  const hex = getDiagramColor(type, served);
  return hex + '1F'; // ~12% opacity
};

/** Label pill colors (solid bg, white text by default). */
export const getDiagramLabelColors = (
  type?: string,
  served?: string
): { bg: string; fg: string } => ({
  bg: getDiagramColor(type, served),
  fg: '#FFFFFF',
});
